import dayjs from 'dayjs';

import { useMemo } from 'react';

import FormHeader from 'components/global/FormHeader';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import { ENUMS, REPORT } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';
import PATHS, { mergePaths } from 'utils/paths';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author Burak Ak
 */

const OrdersByProductReport = () => {
	const defaultSort = useMemo(() => [{ id: 'createdAt', desc: true }], []);

	const columns = useMemo(
		() => [
			{
				Header: 'id',
				accessor: 'ID',
				filterType: FilterType.number,
				width: 50,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'orderID',
				accessor: 'orderID',
				filterType: FilterType.string,
				width: 70,
				showOnMobile: true,
				showCopyButton: true
			},
			{
				Header: 'orderDate',
				accessor: 'createdAt',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME,
				filterRemovable: false
			},
			{
				Header: 'sku',
				accessor: 'SKU',
				filterType: FilterType.string
			},
			{
				Header: 'pickupLocation',
				accessor: 'pickupLocation',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PICKUP_LOCATION
			},
			{
				Header: 'customerName',
				accessor: 'name',
				filterType: FilterType.string
			},
			{
				Header: 'customerPhone',
				accessor: 'telephone',
				filterType: FilterType.string
			},
			{
				Header: 'email',
				accessor: 'emailAddress',
				filterType: FilterType.string
			},
			{
				Header: 'productName',
				accessor: 'productName',
				filterType: FilterType.string
			},

			{
				Header: 'quantity',
				accessor: 'quantity',
				filterType: FilterType.number
			},
			{
				Header: 'packageSize',
				accessor: 'maxPackageSize',
				filterType: FilterType.string
			},
			{ Header: 'customerAddress', accessor: 'address', filterType: FilterType.string, width: 130 },
			{
				Header: 'shippingPerItem',
				accessor: 'shippingPerItem',
				filterType: FilterType.string
			},
			{
				Header: 'brandName',
				accessor: 'storeName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.BRAND_NAME,
				width: 100,
				renderCell: ListComponents.BRAND_LOGO,
				roleName: 'BrandingModule',
				show: false
			},
			{
				Header: 'paymentMethod',
				accessor: 'paymentMethod',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PAYMENT_METHOD_FOR_LIST,
				width: 100
			},
			{
				Header: 'city',
				accessor: 'cityName',
				filterType: FilterType.multiSelection,
				width: 100,
				show: false,
				showOnMobile: true
			},
			{
				Header: 'country',
				accessor: 'countryName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY,
				width: 100,
				show: false
			},
			{
				Header: 'status',
				accessor: 'status',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.ALL_STATUSES,
				filterPrefix: 'status.',
				renderCell: ListComponents.STATUS,
				showOnMobile: true
			},
			{
				Header: 'deliverySlotDate',
				accessor: 'deliverySlotDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATE
			},
			{
				Header: 'deliverySlot',
				accessor: 'deliverySlot',
				filterType: FilterType.string
			},
			{
				Header: 'couponCode',
				accessor: 'couponCode',
				filterType: FilterType.string
			},
			{
				Header: 'price',
				accessor: 'price',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'discountAmount',
				accessor: 'discountAmount',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'tax',
				accessor: 'tax',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'deliveryCompany',
				accessor: 'deliveryName',
				filterType: FilterType.enumMulti,
				width: 90,
				enumName: ENUMS.DELIVERY_COMPANIES_USED,
				renderCell: ListComponents.DC_LOGO
			},
			{
				Header: 'shipmentNumber',
				accessor: 'shipmentNumber',
				filterType: FilterType.string,
				width: 100,
				renderCell: ListComponents.TRACKING_URL
			},
			{
				Header: 'totalDue',
				accessor: 'totalDue',
				filterType: FilterType.float,
				show: false,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'cancelReason',
				accessor: 'cancelReason',
				filterType: FilterType.multiSelection
			},

			{
				Header: 'subtotal',
				accessor: 'subTotal',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'grandTotal',
				accessor: 'grandTotal',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: columns.find((col) => col.accessor === 'createdAt'),
				action: FilterAction.between,
				value: {
					start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
					end: dayjs().endOf('day').toISOString()
				}
			}
		],
		[columns]
	);

	return (
		<div>
			<HeaderContainerFormHeader>
				<FormHeader
					lightTitle="reports"
					boldTitle="ordersByProductReport"
					icon={faArrowLeft}
					backUrl={mergePaths(PATHS.REPORTS, PATHS.ORDER_REPORTS)}
				/>
			</HeaderContainerFormHeader>

			<DynamicList
				id={DYNAMICLISTS.ORDERS_BY_PRODUCT_REPORT}
				columns={columns}
				fetchUrl={REPORT.LIST.GET_ORDERS_BY_PRODUCT_LIST}
				defaultSort={defaultSort}
				defaultFilters={defaultFilters}
				exportName="ordersByProductReport"
				infiniteScroll
			/>
		</div>
	);
};

export default OrdersByProductReport;
